/**
 * bookCollectionAdapter — CollectionAdapter for the book-tracker app.
 * Configures UnifiedCollectionView with book data, filters, sorts, and renderers.
 * Supports multi-user view modes via projectUserFields / _userData.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Card, StarRating, InlineEditCell, formatDate, Pill } from '@code-burrow/design-system';
import { UnifiedCollectionView, useUserView, useUsers, usePod, useCurrentUser, projectUserFields, mergeUserData, filterPodItems, filterToUserItems, isCombinedView, ActivityFeed, ActivityService, CatalogCache } from '@code-burrow/tracker-core';
import BookDetailModal from '../components/BookDetailModal.jsx';

const BOOK_USER_FIELDS = ['status', 'rating', 'startDate', 'finishDate', 'notes'];

function getUserField(item, userName, fieldName) {
    return item._userData?.[userName]?.[fieldName] ?? null;
}

// Returns the YYYY-MM-DD string for the day before the given date string.
// Uses noon UTC to avoid DST/timezone edge cases flipping across midnight.
function dayBefore(dateStr) {
    const d = new Date(dateStr);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().split('T')[0];
}

/**
 * Collect all unique subjects from raw items for filter options.
 */
function collectSubjects(rawItems) {
    const subjects = new Set();
    for (const item of rawItems) {
        if (Array.isArray(item.subjects)) {
            item.subjects.forEach((s) => subjects.add(s));
        }
    }
    return [...subjects].sort();
}

/**
 * Status pill color mapping (bg + text for pill style).
 */
const STATUS_PILL_COLORS = {
    'Reading': 'bg-amber-500/15 text-amber-400',
    'Read': 'bg-emerald-500/15 text-emerald-300',
    'Abandoned': 'bg-red-500/15 text-red-400',
    'On Hold': 'bg-orange-500/15 text-orange-400',
};

function StatusPill({ status }) {
    if (!status) return null;
    return <Pill className={STATUS_PILL_COLORS[status] || 'bg-zinc-500/15 text-zinc-400'}>{status}</Pill>;
}

// Keep plain text colors for table view
const STATUS_COLORS = {
    'Reading': 'text-amber-500',
    'Read': 'text-success',
    'Abandoned': 'text-error',
    'On Hold': 'text-warning',
};

/**
 * BookCollectionView — wraps UnifiedCollectionView with a detail modal.
 */
export function BookCollectionView({ backendService, pocketbaseURL, onItemsLoaded, externalSelect, pageMode = 'thisYear', onSearchOpen, forceRefreshKey = 0 }) {
    const [selectedItem, setSelectedItem] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const { activeView } = useUserView();
    const { users } = useUsers();
    const { pod, allowedUsers, pods } = usePod();
    const currentUser = useCurrentUser();

    // All pod members' avatars are always shown on cards
    const visibleUserIds = useMemo(() => {
        const ids = new Set();
        for (const p of (pods || [])) {
            for (const m of (p.members || [])) {
                if (m.userId !== currentUser?.id) ids.add(m.userId);
            }
        }
        return ids;
    }, [pods, currentUser?.id]);

    useEffect(() => {
        if (externalSelect) setSelectedItem(externalSelect);
    }, [externalSelect]);

    const handleSavedOrDeleted = useCallback(() => {
        setRefreshKey((k) => k + 1);
    }, []);

    useEffect(() => {
        if (forceRefreshKey > 0) setRefreshKey((k) => k + 1);
    }, [forceRefreshKey]);

    const allowedUserIds = useMemo(() => allowedUsers('books'), [allowedUsers]);

    // Activity service for co-participant data + feed
    const activityServiceRef = useRef(null);
    if (!activityServiceRef.current && currentUser?.id) {
        activityServiceRef.current = new ActivityService(currentUser.id);
    }

    const adapter = useMemo(() =>
        bookCollectionAdapter(backendService, pocketbaseURL, setSelectedItem, refreshKey, activeView, pageMode, users, allowedUserIds, pod, onSearchOpen, currentUser, activityServiceRef.current, visibleUserIds, handleSavedOrDeleted),
        [backendService, pocketbaseURL, refreshKey, activeView, pageMode, users, allowedUserIds, pod, onSearchOpen, currentUser, visibleUserIds, handleSavedOrDeleted]
    );
    const feedAdapter = useMemo(() =>
        isCombinedView(activeView) && activityServiceRef.current && pod ? {
            ...adapter,
            feedRenderer: () => (
                <ActivityFeed
                    activityService={activityServiceRef.current}
                    podId={pod.id}
                    users={users}
                    onItemClick={(activity) => {
                        if (activity.itemType === 'book') {
                            setSelectedItem({ id: activity.itemId, title: activity.itemTitle });
                        }
                    }}
                />
            ),
        } : adapter,
        [adapter, activeView, pod, users]
    );

    return (
        <>
            <UnifiedCollectionView collectionAdapter={feedAdapter} onItemsLoaded={onItemsLoaded} refreshKey={`${refreshKey}-${activeView}-${pageMode}-${users.length}`} />
            {selectedItem && (
                <BookDetailModal
                    item={selectedItem}
                    backendService={backendService}
                    pocketbaseURL={pocketbaseURL}
                    onClose={() => setSelectedItem(null)}
                    onSaved={handleSavedOrDeleted}
                    onDeleted={handleSavedOrDeleted}
                />
            )}
        </>
    );
}

export function bookCollectionAdapter(backendService, pocketbaseURL, onItemClick = null, _refreshKey = 0, activeView = 'benny', pageMode = 'thisYear', users = [], allowedUserIds = null, pod = null, onSearchOpen = null, currentUser = null, activityService = null, visibleUserIds = null, onRefresh = null) {
    const currentYear = new Date().getFullYear();
    const isSingleUser = !isCombinedView(activeView);
    const user = isSingleUser ? activeView : null;

    const wasFinishedInYear = (rawItem, year) => {
        const check = (userName) => {
            const d = getUserField(rawItem, userName, 'finishDate');
            return d && new Date(d).getFullYear() === year;
        };
        if (user) return check(user);
        const userNames = users.length > 0 ? users.map((u) => u.name) : ['benny', 'gareth'];
        return userNames.some(check);
    };

    // Collect subjects dynamically during fetchItems
    let allSubjects = [];

    const storageKeyMap = {
        thisYear: 'book-tracker',
        library: 'book-tracker-library',
    };

    const processBookData = (collectionData, coParticipantMap, hasCoParticipantData = true) => {
        let rawBooks = collectionData.items;
        for (const item of rawBooks) {
            item._coParticipantIds = coParticipantMap[item.id] || [];
        }
        if (pageMode === 'pods') {
            rawBooks = filterPodItems(rawBooks, 'books', [], users, 1);
        } else {
            rawBooks = filterToUserItems(rawBooks, activeView);
        }
        allSubjects = collectSubjects(rawBooks);
        let items = rawBooks.map((b) => projectUserFields(b, 'books', activeView, users));

        // ── Pods page: reconcile "together" data with explicit activities ──────
        // PodsPage uses collection overlap (both users have a non-empty status) to
        // determine "Read Together", but _coParticipantIds is the ground truth.
        if (pageMode === 'pods' && currentUser?.name && pod?.members?.length > 0) {
            const podMemberByUserId = Object.fromEntries(pod.members.map((m) => [m.userId, m]));
            const dismissedKey = `pods-dismissed-${currentUser.id}`;
            const dismissed = (() => {
                try { return new Set(JSON.parse(localStorage.getItem(dismissedKey) || '[]')); }
                catch { return new Set(); }
            })();
            for (let idx = 0; idx < items.length; idx++) {
                const item = items[idx];
                const coParticipantIds = new Set(item._coParticipantIds || []);
                const currentUserStatus = item._userData?.[currentUser.name]?.status;
                let newUserData = null;
                let isSyntheticForCurrentUser = false;

                // Part 1: Ensure all explicitly-together participants have a status.
                // A single user marking something "together" is sufficient — synthesize for
                // anyone who hasn't independently added the item yet (including the current user).
                if (coParticipantIds.size > 0) {
                    let referenceStatus = currentUserStatus || null;
                    if (!referenceStatus) {
                        for (const memberId of coParticipantIds) {
                            const m = podMemberByUserId[memberId];
                            if (m) {
                                const s = item._userData?.[m.userName]?.status;
                                if (s) { referenceStatus = s; break; }
                            }
                        }
                    }
                    if (referenceStatus) {
                        if (!currentUserStatus && !dismissed.has(item.id)) {
                            if (!newUserData) newUserData = { ...item._userData };
                            newUserData[currentUser.name] = { ...(item._userData?.[currentUser.name] || {}), status: referenceStatus };
                            isSyntheticForCurrentUser = true;
                        }
                        for (const memberId of coParticipantIds) {
                            const m = podMemberByUserId[memberId];
                            if (!m) continue;
                            const existing = (newUserData || item._userData)?.[m.userName];
                            if (!existing?.status) {
                                if (!newUserData) newUserData = { ...item._userData };
                                newUserData[m.userName] = { ...(item._userData?.[m.userName] || {}), status: referenceStatus };
                            }
                        }
                    }
                }

                // Part 2: Clear status for non-co-participants who have one independently
                // → prevents false "together". finishDate/startDate remain so the item
                // stays in their individual section.
                // Skip when coMaps are empty (cached load) — would incorrectly strip all statuses.
                const effectiveCurrentStatus = (newUserData || item._userData)?.[currentUser.name]?.status;
                if (hasCoParticipantData && effectiveCurrentStatus) {
                    for (const [memberId, member] of Object.entries(podMemberByUserId)) {
                        if (memberId === currentUser.id) continue;
                        if (coParticipantIds.has(memberId)) continue;
                        const mData = (newUserData || item._userData)?.[member.userName];
                        if (mData?.status) {
                            if (!newUserData) newUserData = { ...item._userData };
                            newUserData[member.userName] = { ...mData, status: null };
                        }
                    }
                }

                if (newUserData) {
                    items[idx] = { ...item, _userData: newUserData, ...(isSyntheticForCurrentUser && { _syntheticForCurrentUser: true }) };
                }
            }
        }

        if (activeView === 'pod' && pageMode !== 'pods') {
            const podIds = new Set(filterPodItems(rawBooks, 'books', [], users, pod?.togetherThreshold).map((b) => b.id));
            items = items.filter((item) => podIds.has(item.id));
        }
        return items;
    };

    return {
        storageKey: storageKeyMap[pageMode] || 'book-tracker',
        defaultSortField: pageMode === 'thisYear' ? 'date' : 'title',
        defaultSortDirection: pageMode === 'thisYear' ? 'desc' : 'asc',

        getTabValue: pageMode === 'thisYear'
            ? (item) => {
                if (item.finishDate) return new Date(item.finishDate).getFullYear();
                if (item.startDate) return new Date(item.startDate).getFullYear();
                // Books with no dates show in current year
                return currentYear;
            }
            : null,

        getImageUrl: (item) => {
            const pbImage = item.coverImage;
            return pbImage && pocketbaseURL
                ? `${pocketbaseURL}/api/files/books/${item.id}/${pbImage}`
                : item.coverURL ?? null;
        },

        // ── Data fetching ──────────────────────────────────────────────────────
        fetchItems: async () => {
            const catalogCache = new CatalogCache('books');
            const fullCache = new CatalogCache(`books:${currentUser?.id}`);

            const getCatalog = async () => {
                const c = await catalogCache.get();
                if (c?.items) return c.items;
                const data = await backendService.getCatalog();
                catalogCache.set(data.items).catch(() => {});
                return data.items;
            };

            const [catalogItems, userData, coParticipantMap] = await Promise.all([
                getCatalog(),
                backendService.getUserData(currentUser?.id),
                activityService ? activityService.getCoParticipantMap('book') : Promise.resolve({}),
            ]);

            // Client-side merge
            let items = [...catalogItems];
            const allowedIds = new Set((userData.users || []).map((u) => u.id));
            for (const [, jt] of Object.entries(userData.joinData || {})) {
                items = mergeUserData(items, jt.records, jt.itemIdField, jt.userFields, userData.users, allowedIds);
            }
            const collectionData = { items, users: userData.users };
            fullCache.set(collectionData).catch(() => {});
            return processBookData(collectionData, coParticipantMap);
        },

        getCachedItems: async () => {
            const fullCache = new CatalogCache(`books:${currentUser?.id}`);
            const cached = await fullCache.getEvenIfStale();
            if (!cached?.items) return null;
            try {
                const items = processBookData(cached.items, {}, false);
                return { items, isStale: cached.isStale, timestamp: cached.timestamp };
            } catch { return null; }
        },

        // ── Context menu ──────────────────────────────────────────────────────
        backendService,
        activeView,
        getContextMenuActions: (item) => [
            {
                label: 'Refresh Metadata',
                onClick: async () => {
                    const result = await backendService.refreshBook(item.id);
                    if (result && !result.success) throw new Error(result.error);
                },
            },
            {
                label: 'Delete',
                variant: 'danger',
                onClick: async () => {
                    try {
                        const joins = await backendService.db.pb.collection('user_books').getFullList({
                            filter: `book="${item.id}"`, $autoCancel: false,
                        });
                        for (const j of joins) await backendService.proxy.collection('user_books').delete(j.id);
                    } catch {}
                    await backendService.proxy.collection('books').delete(item.id);
                },
            },
        ],

        // ── Filter fields ──────────────────────────────────────────────────────
        filterFields: [
            {
                key: 'status',
                label: 'Status',
                type: 'multiselect',
                allLabel: 'All Statuses',
                targetFields: ['status'],
                staticOptions: [
                    { value: 'Reading', label: 'Reading' },
                    { value: 'Read', label: 'Read' },
                    { value: 'Abandoned', label: 'Abandoned' },
                    { value: 'On Hold', label: 'On Hold' },
                ],
            },
            {
                key: 'subjects',
                label: 'Genre / Subject',
                type: 'select',
                targetFields: [],
                getOptions: () => allSubjects.map((s) => ({ value: s, label: s })),
                filterFn: (item, value) => {
                    return Array.isArray(item.subjects) && item.subjects.includes(value);
                },
            },
        ],

        // ── Sort fields ────────────────────────────────────────────────────────
        sortFields: [
            {
                key: 'title',
                label: 'Title',
                getValue: (item) => (item.title ?? '').toLowerCase(),
            },
            {
                key: 'author',
                label: 'Author',
                getValue: (item) => (item.author ?? '').toLowerCase(),
            },
            {
                key: 'rating',
                label: 'Rating',
                getValue: (item) => item.rating ?? null,
            },
            {
                key: 'date',
                label: 'Date',
                getValue: (item) => {
                    const d = item.finishDate || item.startDate;
                    return d ? new Date(d).getTime() : null;
                },
            },
            {
                key: 'pageCount',
                label: 'Page Count',
                getValue: (item) => item.pageCount ?? null,
            },
        ],

        // ── Grid item renderer ─────────────────────────────────────────────────
        renderGridItem: (item) => {
            const pbImage = item.coverImage;
            const imageURL = pbImage && pocketbaseURL
                ? `${pocketbaseURL}/api/files/books/${item.id}/${pbImage}`
                : item.coverURL ?? null;

            const status = item.status;

            // Date display — prefer finishDate, fall back to startDate
            let dateStr;
            if (isCombinedView(activeView)) {
                const userDates = (users.length > 0 ? users : []).map((u) => ({
                    name: u.name,
                    emoji: u.emoji,
                    date: getUserField(item, u.name, 'finishDate') || getUserField(item, u.name, 'startDate') || null,
                })).filter((ud) => ud.date);

                const allSame = userDates.length > 1 &&
                    userDates.every((ud) => formatDate(ud.date) === formatDate(userDates[0].date));

                if (userDates.length > 0) {
                    if (allSame) {
                        dateStr = formatDate(userDates[0].date);
                    } else {
                        dateStr = userDates.map((ud) => `${ud.emoji} ${formatDate(ud.date)}`).join('  ');
                    }
                }
            } else {
                dateStr = item.finishDate ? formatDate(item.finishDate)
                    : item.startDate ? formatDate(item.startDate) : null;
            }

            // Status pill (inline with avatars)
            const statusPillContent = status ? (
                <span className="-ml-0.5"><StatusPill status={status} /></span>
            ) : null;

            // Date + rating (below avatar row, full card width)
            const statusMetaContent = (dateStr || item.rating > 0) ? (
                <>
                    {dateStr && (
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-text-tertiary">{dateStr}</span>
                        </div>
                    )}
                    {item.rating > 0 && (
                        <div className="mt-1">
                            <StarRating rating={item.rating} />
                        </div>
                    )}
                </>
            ) : null;

            // Two-tier avatar system
            const coPartIds = item._coParticipantIds || [];
            const coPartIdSet = new Set(coPartIds);
            const myAvatar = currentUser ? { emoji: users.find(u => u.name === currentUser.name)?.emoji, name: currentUser.name } : null;

            let coParticipants = undefined;
            if (coPartIds.length > 0 && myAvatar?.emoji) {
                const visibleCoUsers = users.filter(u => coPartIdSet.has(u.id) && (!visibleUserIds || visibleUserIds.has(u.id)));
                if (visibleCoUsers.length > 0) {
                    coParticipants = [
                        myAvatar,
                        ...visibleCoUsers.map(u => ({ emoji: u.emoji, name: u.displayName || u.name })),
                    ];
                }
            }

            // Wrap with avatar tiers for compound Card pattern
            const statusWithAvatars = (
                <>
                    {(coParticipants?.length > 0 || statusPillContent) && (
                        <div className="mt-2">
                            <div className="flex items-center gap-1.5">
                                {coParticipants?.length > 0 && (
                                    <div className="flex -space-x-1.5 flex-shrink-0">
                                        {coParticipants.slice(0, 4).map((p, i) => (
                                            <span key={i} className="w-5 h-5 rounded-full bg-bg-secondary border border-surface text-[10px] flex items-center justify-center" title={p.name}>{p.emoji}</span>
                                        ))}
                                    </div>
                                )}
                                {statusPillContent && <div className="min-w-0 flex-1">{statusPillContent}</div>}
                            </div>
                        </div>
                    )}
                    {statusMetaContent && (
                        <div className="mt-1">{statusMetaContent}</div>
                    )}
                    {pageMode === 'pods' && item._syntheticForCurrentUser && onRefresh && (() => {
                        const coUser = users.find(u => item._coParticipantIds?.includes(u.id));
                        const coData = coUser ? item._userData?.[coUser.name] : null;
                        const dismissedKey = `pods-dismissed-${currentUser.id}`;
                        return (
                            <div className="flex gap-1.5 mt-2">
                                <button
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                            await backendService.upsertUserBook(currentUser.name, item.id, {
                                                status: coData?.status || item._userData?.[currentUser.name]?.status || 'Read',
                                                ...(coData?.finishDate && { finishDate: coData.finishDate }),
                                            });
                                            onRefresh();
                                        } catch {}
                                    }}
                                    className="text-xs px-2 py-0.5 rounded bg-accent-primary/20 text-accent-primary hover:bg-accent-primary/30 transition-colors"
                                >
                                    Add to my list
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        try {
                                            const set = new Set(JSON.parse(localStorage.getItem(dismissedKey) || '[]'));
                                            set.add(item.id);
                                            localStorage.setItem(dismissedKey, JSON.stringify([...set]));
                                        } catch {}
                                        onRefresh();
                                    }}
                                    className="text-xs px-2 py-0.5 rounded bg-surface-secondary text-text-tertiary hover:text-text-primary transition-colors"
                                >
                                    Dismiss
                                </button>
                            </div>
                        );
                    })()}
                </>
            );

            return (
                <Card
                    key={item.id}
                    imageAlt={item.title}
                    title={item.title}
                    subtitle={item.author}
                    onClick={onItemClick ? () => onItemClick({ ...item, imageURL }) : undefined}
                    className="[&_.aspect-video]:!aspect-[2/3]"
                >
                    {/* Book cover — fixed height so all cards align */}
                    <div className="relative w-full h-64 md:h-72 lg:h-80 bg-bg-tertiary rounded-t-lg overflow-hidden flex items-center justify-center py-2 px-4">
                        {imageURL ? (
                            <img
                                src={imageURL}
                                alt={item.title}
                                className="h-full w-full object-contain shadow-md"
                                loading="lazy"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <svg className="w-16 h-16 text-text-tertiary" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                                </svg>
                            </div>
                        )}
                    </div>
                    <Card.Content>
                        <Card.Title title={item.title} />
                        <Card.Subtitle subtitle={item.author} />
                        {statusWithAvatars}
                    </Card.Content>
                </Card>
            );
        },

        // ── Table columns ──────────────────────────────────────────────────────
        renderTableColumns: buildBookTableColumns(backendService, pocketbaseURL, onItemClick, activeView, users),

        // ── Bulk edit ────────────────────────────────────────────────────────
        getItemId: (item) => item.id,
        bulkEditFields: isCombinedView(activeView) ? [] : [
            { key: 'rating', label: 'Rating', type: 'rating' },
            {
                key: 'status', label: 'Status', type: 'select',
                options: [
                    { value: 'Reading',   label: 'Reading',   pillClassName: STATUS_PILL_COLORS['Reading'] },
                    { value: 'Read',      label: 'Read',      pillClassName: STATUS_PILL_COLORS['Read'] },
                    { value: 'Abandoned', label: 'Abandoned', pillClassName: STATUS_PILL_COLORS['Abandoned'] },
                    { value: 'On Hold',   label: 'On Hold',   pillClassName: STATUS_PILL_COLORS['On Hold'] },
                ],
            },
        ],
        onBulkSave: async (selectedItems, updates) => {
            const errors = [];
            for (const item of selectedItems) {
                try {
                    await backendService.upsertUserBook(activeView, item.id, updates);
                } catch (err) {
                    errors.push({ title: item.title, error: err.message });
                }
            }
            if (errors.length > 0) throw new Error(`Failed to update ${errors.length} item(s)`);
        },

        // ── Empty state ────────────────────────────────────────────────────────
        emptyState: (() => {
            const isOtherUser = !isCombinedView(activeView) && activeView !== currentUser?.name;
            const viewUser = isOtherUser ? users.find((u) => u.name === activeView) : null;
            const searchBtn = onSearchOpen ? (
                <button onClick={onSearchOpen} className="px-4 py-2 rounded-md bg-accent-primary text-text-inverse text-sm font-medium hover:brightness-90 transition-all">
                    Search & Add
                </button>
            ) : undefined;

            if (isOtherUser) return {
                title: `No books shared by ${viewUser?.displayName || activeView} yet`,
            };
            if (activeView === 'pod') return {
                title: 'No books read together yet',
                description: 'Books your pod has read will appear here',
            };
            if (pageMode === 'thisYear') return {
                title: 'No books read yet',
                description: 'Books you read will show up here',
                action: searchBtn,
            };
            return {
                title: 'No books yet',
                description: !pod
                    ? 'Search to add your first book. You can also create a Pod in Settings to share with friends.'
                    : 'Get started by adding a book to your collection',
                action: searchBtn,
            };
        })(),

        actions: [],
    };
}

// ── Table column builder ───────────────────────────────────────────────────────

function buildBookTableColumns(backendService, pocketbaseURL, onItemClick, activeView, users) {
    const handleSave = async (item, field, value) => {
        const updates = { [field]: value };
        if (field === 'finishDate' && value) {
            if (item.startDate && item.startDate > value) updates.startDate = dayBefore(value);
            if (item.status !== 'Read') updates.status = 'Read';
        }
        await backendService.upsertUserBook(activeView, item.id, updates);
    };

    return [
        {
            key: 'image',
            header: '',
            width: '50px',
            renderCell: (item) => {
                const pbImage = item.coverImage;
                const url = pbImage && pocketbaseURL
                    ? `${pocketbaseURL}/api/files/books/${item.id}/${pbImage}`
                    : item.coverURL ?? null;
                return url ? (
                    <img src={url} alt={item.title} className="h-14 w-10 object-cover rounded-sm" loading="lazy" />
                ) : (
                    <div className="h-14 w-10 bg-bg-tertiary rounded-sm flex items-center justify-center">
                        <svg className="w-5 h-5 text-text-tertiary" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                        </svg>
                    </div>
                );
            },
        },
        {
            key: 'title',
            header: 'Title',
            sortable: 'title',
            renderCell: (item) => {
                const content = (
                    <div className="flex flex-col">
                        <span className="font-medium text-text-primary">{item.title}</span>
                        {item.author && <span className="text-xs text-text-tertiary">{item.author}</span>}
                    </div>
                );
                return onItemClick ? (
                    <button onClick={() => onItemClick(item)} className="hover:text-accent-primary transition-colors duration-150 text-left">
                        {content}
                    </button>
                ) : content;
            },
        },
        {
            key: 'author',
            header: 'Author',
            sortable: 'author',
            width: '150px',
            renderCell: (item) => (
                <span className="text-text-secondary text-sm">{item.author || '-'}</span>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            sortable: 'status',
            width: '140px',
            renderCell: (item, { onItemUpdated } = {}) => (
                <InlineEditCell
                    item={item}
                    field="status"
                    editType="select"
                    options={[
                        { value: 'Reading',   label: 'Reading',   pillClassName: STATUS_PILL_COLORS['Reading'] },
                        { value: 'Read',      label: 'Read',      pillClassName: STATUS_PILL_COLORS['Read'] },
                        { value: 'On Hold',   label: 'On Hold',   pillClassName: STATUS_PILL_COLORS['On Hold'] },
                        { value: 'Abandoned', label: 'Abandoned', pillClassName: STATUS_PILL_COLORS['Abandoned'] },
                    ]}
                    displayValue={item.status ? <StatusPill status={item.status} /> : <span className="text-text-tertiary">-</span>}
                    onSave={async (it, field, value) => { await handleSave(it, field, value); onItemUpdated?.(it, field, value); }}
                    readOnly={isCombinedView(activeView)}
                />
            ),
        },
        {
            key: 'rating',
            header: 'Rating',
            sortable: 'rating',
            width: '120px',
            renderCell: (item, { onItemUpdated } = {}) => (
                <InlineEditCell
                    item={item}
                    field="rating"
                    editType="rating"
                    displayValue={<StarRating rating={item.rating} />}
                    onSave={async (it, field, value) => { await handleSave(it, field, value); onItemUpdated?.(it, field, value); }}
                    readOnly={isCombinedView(activeView)}
                />
            ),
        },
        {
            key: 'finishDate',
            header: 'Finished',
            sortable: 'finishDate',
            width: '130px',
            renderCell: (item, { onItemUpdated } = {}) => (
                <InlineEditCell
                    item={item}
                    field="finishDate"
                    editType="date"
                    displayValue={formatDate(item.finishDate)}
                    onSave={async (it, field, value) => { await handleSave(it, field, value); onItemUpdated?.(it, field, value); }}
                    readOnly={isCombinedView(activeView)}
                />
            ),
        },
        {
            key: 'pageCount',
            header: 'Pages',
            sortable: 'pageCount',
            width: '80px',
            renderCell: (item) => (
                <span className="text-text-secondary">{item.pageCount || '-'}</span>
            ),
        },
    ];
}
